/**
 * SRP (Search Results Page) Allowlist
 * 
 * Defines which search result pages are indexable by search engines.
 * Only pages in this allowlist will have:
 * - robots: index,follow
 * - Self-referencing canonical
 * - Proper hreflang cluster
 * 
 * All other pages will be noindex,follow
 */

export interface SRPHub {
  type: 'location' | 'role';
  localeUrls: {
    en: string;
    fi: string;
    sv: string;
  };
  title: {
    en: string;
    fi: string;
    sv: string;
  };
  description: {
    en: string;
    fi: string;
    sv: string;
  };
  lastModified: string; // ISO date string
}

/**
 * Major Finnish cities
 */
const cityHubs: SRPHub[] = [
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Helsinki_Finland',
      fi: '/fi/tyopaikat/alue/Helsinki_Suomi',
      sv: '/sv/lediga-jobb/omrade/Helsingfors_Finland'
    },
    title: {
      en: 'Jobs in Helsinki',
      fi: 'Työpaikat Helsingissä',
      sv: 'Jobb i Helsingfors'
    },
    description: {
      en: 'Discover open positions in Helsinki, Finland\'s vibrant capital. From tech startups to established corporations, find your next career opportunity in the Nordic hub of innovation and design.',
      fi: 'Löydä avoimia työpaikkoja Helsingistä, Suomen elinvoimaisesta pääkaupungista. Teknologiastartupeista vakiintuneisiin yrityksiin - löydä seuraava urasi innovaation ja muotoilun pohjoismaisen keskuksen ytimessä.',
      sv: 'Upptäck öppna tjänster i Helsingfors, Finlands livliga huvudstad. Från tech-startups till etablerade företag, hitta din nästa karriärmöjlighet i det nordiska navet för innovation och design.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Espoo_Finland',
      fi: '/fi/tyopaikat/alue/Espoo_Suomi',
      sv: '/sv/lediga-jobb/omrade/Esbo_Finland'
    },
    title: {
      en: 'Jobs in Espoo',
      fi: 'Työpaikat Espoossa',
      sv: 'Jobb i Esbo'
    },
    description: {
      en: 'Explore career opportunities in Espoo, home to leading tech companies, research institutions, and innovative startups. Find your place in Finland\'s second-largest city and a major tech hub.',
      fi: 'Tutki uramahdollisuuksia Espoossa, johtavien teknologiayritysten, tutkimuslaitosten ja innovatiivisten startup-yritysten kotikaupungissa. Löydä paikkasi Suomen toiseksi suurimmasta kaupungista ja merkittävästä teknologiakeskuksesta.',
      sv: 'Utforska karriärmöjligheter i Esbo, hem för ledande teknikföretag, forskningsinstitutioner och innovativa startups. Hitta din plats i Finlands näst största stad och ett stort teknikcentrum.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Tampere_Finland',
      fi: '/fi/tyopaikat/alue/Tampere_Suomi',
      sv: '/sv/lediga-jobb/omrade/Tammerfors_Finland'
    },
    title: {
      en: 'Jobs in Tampere',
      fi: 'Työpaikat Tampereella',
      sv: 'Jobb i Tammerfors'
    },
    description: {
      en: 'Find jobs in Tampere, Finland\'s third-largest city known for its vibrant culture, growing tech scene, and excellent quality of life. Explore opportunities in manufacturing, technology, and creative industries.',
      fi: 'Löydä työpaikkoja Tampereelta, Suomen kolmanneksi suurimmasta kaupungista, joka tunnetaan elinvoimaisesta kulttuuristaan, kasvavasta teknologia-alastaan ja erinomaisesta elämänlaadusta. Tutustu mahdollisuuksiin teollisuudessa, teknologiassa ja luovilla aloilla.',
      sv: 'Hitta jobb i Tammerfors, Finlands tredje största stad känd för sin livliga kultur, växande teknikscen och utmärkta livskvalitet. Utforska möjligheter inom tillverkning, teknik och kreativa branscher.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Vantaa_Finland',
      fi: '/fi/tyopaikat/alue/Vantaa_Suomi',
      sv: '/sv/lediga-jobb/omrade/Vanda_Finland'
    },
    title: {
      en: 'Jobs in Vantaa',
      fi: 'Työpaikat Vantaalla',
      sv: 'Jobb i Vanda'
    },
    description: {
      en: 'Discover career opportunities in Vantaa, part of the Greater Helsinki metropolitan area. Home to Helsinki Airport and major logistics hubs, Vantaa offers diverse opportunities in aviation, logistics, and services.',
      fi: 'Löydä uramahdollisuuksia Vantaalta, osana pääkaupunkiseutua. Helsinki-Vantaan lentokentän ja merkittävien logistiikkakeskusten kotikaupunki tarjoaa monipuolisia mahdollisuuksia ilmailussa, logistiikassa ja palveluissa.',
      sv: 'Upptäck karriärmöjligheter i Vanda, del av Helsingfors storstadsområde. Hem för Helsingfors flygplats och stora logistikcentra, Vanda erbjuder olika möjligheter inom flyg, logistik och tjänster.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Turku_Finland',
      fi: '/fi/tyopaikat/alue/Turku_Suomi',
      sv: '/sv/lediga-jobb/omrade/Åbo_Finland'
    },
    title: {
      en: 'Jobs in Turku',
      fi: 'Työpaikat Turussa',
      sv: 'Jobb i Åbo'
    },
    description: {
      en: 'Explore job opportunities in Turku, Finland\'s oldest city and a major port. Find positions in maritime industries, healthcare, education, and emerging tech sectors in this historic coastal city.',
      fi: 'Tutki työmahdollisuuksia Turussa, Suomen vanhimmassa kaupungissa ja merkittävässä satamassa. Löydä työpaikkoja merenkulkualoilta, terveydenhuollosta, koulutuksesta ja nousevista teknologia-aloista tässä historiallisessa rannikkokaupungissa.',
      sv: 'Utforska jobbmöjligheter i Åbo, Finlands äldsta stad och en stor hamn. Hitta positioner inom maritima industrier, hälsovård, utbildning och framväxande tekniska sektorer i denna historiska kuststaden.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Oulu_Finland',
      fi: '/fi/tyopaikat/alue/Oulu_Suomi',
      sv: '/sv/lediga-jobb/omrade/Uleåborg_Finland'
    },
    title: {
      en: 'Jobs in Oulu',
      fi: 'Työpaikat Oulussa',
      sv: 'Jobb i Uleåborg'
    },
    description: {
      en: 'Find jobs in Oulu, Northern Finland\'s tech capital with a strong focus on ICT, cleantech, and wellbeing technologies. Join a thriving ecosystem of innovation and research.',
      fi: 'Löydä työpaikkoja Oulusta, Pohjois-Suomen teknologiapääkaupungista, jossa keskitytään vahvasti ICT:hen, puhtaaseen teknologiaan ja hyvinvointiteknologioihin. Liity kukoistavaan innovaatio- ja tutkimusekosysteemiin.',
      sv: 'Hitta jobb i Uleåborg, norra Finlands teknikkapital med stark fokus på IKT, cleantech och välfärdsteknologier. Gå med i ett blomstrande ekosystem av innovation och forskning.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Jyväskylä_Finland',
      fi: '/fi/tyopaikat/alue/Jyväskylä_Suomi',
      sv: '/sv/lediga-jobb/omrade/Jyväskylä_Finland'
    },
    title: {
      en: 'Jobs in Jyväskylä',
      fi: 'Työpaikat Jyväskylässä',
      sv: 'Jobb i Jyväskylä'
    },
    description: {
      en: 'Discover opportunities in Jyväskylä, a vibrant university city in Central Finland. Known for education, research, and paper industry, find your next role in this dynamic lakeside city.',
      fi: 'Löydä mahdollisuuksia Jyväskylästä, elinvoimaisesta yliopistokaupungista Keski-Suomessa. Koulutuksestaan, tutkimuksestaan ja paperiteollisuudestaan tunnettu - löydä seuraava roolisi tästä dynaamisesta järven rannalla sijaitsevasta kaupungista.',
      sv: 'Upptäck möjligheter i Jyväskylä, en livlig universitetsstad i Mellersta Finland. Känd för utbildning, forskning och pappersindustri, hitta din nästa roll i denna dynamiska sjöstad.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Lahti_Finland',
      fi: '/fi/tyopaikat/alue/Lahti_Suomi',
      sv: '/sv/lediga-jobb/omrade/Lahtis_Finland'
    },
    title: {
      en: 'Jobs in Lahti',
      fi: 'Työpaikat Lahdessa',
      sv: 'Jobb i Lahtis'
    },
    description: {
      en: 'Find career opportunities in Lahti, a growing city known for environmental innovation and circular economy. Explore positions in green technology, design, and sustainable business.',
      fi: 'Löydä uramahdollisuuksia Lahdesta, kasvavasta kaupungista, joka tunnetaan ympäristöinnovaatioistaan ja kiertotaloudesta. Tutustu työpaikkoihin vihreässä teknologiassa, muotoilussa ja kestävässä liiketoiminnassa.',
      sv: 'Hitta karriärmöjligheter i Lahtis, en växande stad känd för miljöinnovation och cirkulär ekonomi. Utforska positioner inom grön teknologi, design och hållbar affärsverksamhet.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Kuopio_Finland',
      fi: '/fi/tyopaikat/alue/Kuopio_Suomi',
      sv: '/sv/lediga-jobb/omrade/Kuopio_Finland'
    },
    title: {
      en: 'Jobs in Kuopio',
      fi: 'Työpaikat Kuopiossa',
      sv: 'Jobb i Kuopio'
    },
    description: {
      en: 'Explore job openings in Kuopio, Eastern Finland\'s regional capital and a center for health technology and wellbeing. Find opportunities in healthcare, food industry, and technology sectors.',
      fi: 'Tutki työpaikkoja Kuopiosta, Itä-Suomen aluekeskuksesta ja terveysteknologian sekä hyvinvoinnin keskuksesta. Löydä mahdollisuuksia terveydenhuollosta, elintarviketeollisuudesta ja teknologia-aloilta.',
      sv: 'Utforska jobböppningar i Kuopio, östra Finlands regionala huvudstad och ett centrum för hälsoteknik och välbefinnande. Hitta möjligheter inom hälsovård, livsmedelsindustri och teknologisektorer.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Pori_Finland',
      fi: '/fi/tyopaikat/alue/Pori_Suomi',
      sv: '/sv/lediga-jobb/omrade/Björneborg_Finland'
    },
    title: {
      en: 'Jobs in Pori',
      fi: 'Työpaikat Porissa',
      sv: 'Jobb i Björneborg'
    },
    description: {
      en: 'Discover career opportunities in Pori, a coastal city with strong industry and energy sectors. Find positions in energy, manufacturing, logistics, and cultural industries.',
      fi: 'Löydä uramahdollisuuksia Porista, rannikkokaupungista, jossa on vahva teollisuus- ja energiasektori. Löydä työpaikkoja energiasta, valmistuksesta, logistiikasta ja kulttuurialoilta.',
      sv: 'Upptäck karriärmöjligheter i Björneborg, en kuststad med starka industri- och energisektorer. Hitta positioner inom energi, tillverkning, logistik och kulturindustrier.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Kotka_Finland',
      fi: '/fi/tyopaikat/alue/Kotka_Suomi',
      sv: '/sv/lediga-jobb/omrade/Kotka_Finland'
    },
    title: {
      en: 'Jobs in Kotka',
      fi: 'Työpaikat Kotkassa',
      sv: 'Jobb i Kotka'
    },
    description: {
      en: 'Discover career opportunities in Kotka, a major port city on the Gulf of Finland specializing in logistics, maritime industries, and international trade. With its strategic coastal location and strong industrial base, Kotka offers excellent opportunities in transportation and manufacturing sectors.',
      fi: 'Löydä uramahdollisuuksia Kotkasta, Suomenlahden tärkeästä satamakaupungista, joka erikoistuu logistiikkaan, meriteollisuuteen ja kansainväliseen kauppaan. Strategisella rannikkosijainnillaan ja vahvalla teollisuusperinteellä Kotka tarjoaa erinomaisia mahdollisuuksia liikenteen ja valmistavan teollisuuden aloilla.',
      sv: 'Upptäck karriärmöjligheter i Kotka, en viktig hamnstad vid Finska viken som specialiserar sig på logistik, sjöindustri och internationell handel. Med sin strategiska kustplats och starka industriella bas erbjuder Kotka utmärkta möjligheter inom transport och tillverkningssektorer.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  }
];

/**
 * Top job roles
 */
const roleHubs: SRPHub[] = [
  {
    type: 'role',
    localeUrls: {
      en: '/en/jobs/role/Software_Engineer',
      fi: '/fi/tyopaikat/rooli/Ohjelmistokehittäjä',
      sv: '/sv/lediga-jobb/roll/Mjukvaruingenjör'
    },
    title: {
      en: 'Software Engineer Jobs',
      fi: 'Ohjelmistokehittäjän työpaikat',
      sv: 'Mjukvaruingenjörsjobb'
    },
    description: {
      en: 'Find software engineer positions across Finland. From startups to enterprises, discover opportunities in full-stack development, backend engineering, and software architecture.',
      fi: 'Löydä ohjelmistokehittäjän työpaikkoja ympäri Suomen. Startupeista suuryrityksiin - löydä mahdollisuuksia full-stack-kehityksessä, backend-ohjelmoinnissa ja ohjelmistoarkkitehtuurissa.',
      sv: 'Hitta mjukvaruingenjörspositioner över Finland. Från startups till företag, upptäck möjligheter inom full-stack-utveckling, backend-teknik och mjukvaruarkitektur.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'role',
    localeUrls: {
      en: '/en/jobs/role/Frontend_Developer',
      fi: '/fi/tyopaikat/rooli/Frontend-kehittäjä',
      sv: '/sv/lediga-jobb/roll/Frontend-utvecklare'
    },
    title: {
      en: 'Frontend Developer Jobs',
      fi: 'Frontend-kehittäjän työpaikat',
      sv: 'Frontend-utvecklarjobb'
    },
    description: {
      en: 'Browse frontend developer opportunities in Finland. Build modern web applications using React, Vue, Angular, and other cutting-edge frameworks.',
      fi: 'Selaa frontend-kehittäjän mahdollisuuksia Suomessa. Rakenna moderneja web-sovelluksia käyttäen Reactia, Vuea, Angularia ja muita huippuluokan kehyksiä.',
      sv: 'Bläddra bland frontend-utvecklarmöjligheter i Finland. Bygg moderna webbapplikationer med React, Vue, Angular och andra banbrytande ramverk.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'role',
    localeUrls: {
      en: '/en/jobs/role/Data_Scientist',
      fi: '/fi/tyopaikat/rooli/Data-tieteilijä',
      sv: '/sv/lediga-jobb/roll/Datavetare'
    },
    title: {
      en: 'Data Scientist Jobs',
      fi: 'Data-tieteilijän työpaikat',
      sv: 'Datavetarjobb'
    },
    description: {
      en: 'Explore data scientist roles in Finland. Work with machine learning, AI, and data analytics to solve complex business problems and drive innovation.',
      fi: 'Tutki data-tieteilijän rooleja Suomessa. Työskentele koneoppimisen, tekoälyn ja data-analytiikan parissa ratkaistaksesi monimutkaisia liiketoimintaongelmia ja edistääksesi innovaatioita.',
      sv: 'Utforska datavetarroller i Finland. Arbeta med maskininlärning, AI och dataanalys för att lösa komplexa affärsproblem och driva innovation.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'role',
    localeUrls: {
      en: '/en/jobs/role/Product_Manager',
      fi: '/fi/tyopaikat/rooli/Tuotepäällikkö',
      sv: '/sv/lediga-jobb/roll/Produktchef'
    },
    title: {
      en: 'Product Manager Jobs',
      fi: 'Tuotepäällikön työpaikat',
      sv: 'Produktchefsjobb'
    },
    description: {
      en: 'Find product manager positions in Finland. Lead product development, define strategy, and drive business growth in tech companies and startups.',
      fi: 'Löydä tuotepäällikön työpaikkoja Suomessa. Johda tuotekehitystä, määrittele strategia ja edistä liiketoiminnan kasvua teknologiayrityksissä ja startupeissa.',
      sv: 'Hitta produktchefspositioner i Finland. Led produktutveckling, definiera strategi och driv affärstillväxt i teknikföretag och startups.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'role',
    localeUrls: {
      en: '/en/jobs/role/UX_Designer',
      fi: '/fi/tyopaikat/rooli/UX-suunnittelija',
      sv: '/sv/lediga-jobb/roll/UX-designer'
    },
    title: {
      en: 'UX Designer Jobs',
      fi: 'UX-suunnittelijan työpaikat',
      sv: 'UX-designerjobb'
    },
    description: {
      en: 'Discover UX designer opportunities in Finland. Create user-centered designs, conduct research, and shape digital experiences for leading companies.',
      fi: 'Löydä UX-suunnittelijan mahdollisuuksia Suomessa. Luo käyttäjäkeskeisiä suunnitelmia, tee tutkimusta ja muotoile digitaalisia kokemuksia johtaville yrityksille.',
      sv: 'Upptäck UX-designermöjligheter i Finland. Skapa användarcentrerade designer, genomför forskning och forma digitala upplevelser för ledande företag.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'role',
    localeUrls: {
      en: '/en/jobs/role/DevOps_Engineer',
      fi: '/fi/tyopaikat/rooli/DevOps-insinööri',
      sv: '/sv/lediga-jobb/roll/DevOps-ingenjör'
    },
    title: {
      en: 'DevOps Engineer Jobs',
      fi: 'DevOps-insinöörin työpaikat',
      sv: 'DevOps-ingenjörsjobb'
    },
    description: {
      en: 'Browse DevOps engineer positions in Finland. Build and maintain cloud infrastructure, automate deployments, and enable continuous delivery.',
      fi: 'Selaa DevOps-insinöörin työpaikkoja Suomessa. Rakenna ja ylläpidä pilvialustoja, automatisoi käyttöönottoja ja mahdollista jatkuva toimitus.',
      sv: 'Bläddra bland DevOps-ingenjörspositioner i Finland. Bygg och underhåll molninfrastruktur, automatisera driftsättningar och möjliggör kontinuerlig leverans.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  }
];

/**
 * Remote work hubs
 */
const remoteHubs: SRPHub[] = [
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Remote_-_Finland',
      fi: '/fi/tyopaikat/alue/Etätyö_-_Suomi',
      sv: '/sv/lediga-jobb/omrade/Distans_-_Finland'
    },
    title: {
      en: 'Remote Jobs in Finland',
      fi: 'Etätyöpaikat Suomessa',
      sv: 'Distansjobb i Finland'
    },
    description: {
      en: 'Find remote job opportunities based in Finland. Work from anywhere while being employed by Finnish companies offering flexible work arrangements and competitive benefits.',
      fi: 'Löydä etätyömahdollisuuksia suomalaisissa yrityksissä. Työskentele mistä tahansa samalla kun olet suomalaisten yritysten palveluksessa, jotka tarjoavat joustavia työjärjestelyjä ja kilpailukykyisiä etuja.',
      sv: 'Hitta distansjobbmöjligheter baserade i Finland. Arbeta var som helst medan du är anställd av finska företag som erbjuder flexibla arbetsarrangemang och konkurrenskraftiga förmåner.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/Remote_-_Europe',
      fi: '/fi/tyopaikat/alue/Etätyö_-_Eurooppa',
      sv: '/sv/lediga-jobb/omrade/Distans_-_Europa'
    },
    title: {
      en: 'Remote Jobs in Europe',
      fi: 'Etätyöpaikat Euroopassa',
      sv: 'Distansjobb i Europa'
    },
    description: {
      en: 'Explore remote job opportunities across Europe. Find positions with European companies offering location-independent work and international career growth.',
      fi: 'Tutki etätyömahdollisuuksia ympäri Eurooppaa. Löydä työpaikkoja eurooppalaisista yrityksistä, jotka tarjoavat sijainnnista riippumatonta työtä ja kansainvälistä urakebitystä.',
      sv: 'Utforska distansjobbmöjligheter över Europa. Hitta positioner hos europeiska företag som erbjuder platsoberoende arbete och internationell karriärtillväxt.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  }
];

/**
 * Complete allowlist of indexable SRPs
 */
export const SRP_ALLOWLIST: SRPHub[] = [
  ...cityHubs,
  ...roleHubs,
  ...remoteHubs
];

/**
 * Check if a given path is in the allowlist
 */
export function isAllowlistedSRP(path: string): boolean {
  // Normalize path (remove trailing slash, ensure leading slash)
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');
  
  // Decode URL encoding for comparison (handles %C3%A5 etc.)
  const decodedPath = decodeURIComponent(normalizedPath);
  
  return SRP_ALLOWLIST.some(hub => 
    hub.localeUrls.en === normalizedPath ||
    hub.localeUrls.en === decodedPath ||
    hub.localeUrls.fi === normalizedPath ||
    hub.localeUrls.fi === decodedPath ||
    hub.localeUrls.sv === normalizedPath ||
    hub.localeUrls.sv === decodedPath
  );
}

/**
 * Get SRP hub data for a given path
 */
export function getSRPHub(path: string): SRPHub | null {
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');
  const decodedPath = decodeURIComponent(normalizedPath);
  
  return SRP_ALLOWLIST.find(hub => 
    hub.localeUrls.en === normalizedPath ||
    hub.localeUrls.en === decodedPath ||
    hub.localeUrls.fi === normalizedPath ||
    hub.localeUrls.fi === decodedPath ||
    hub.localeUrls.sv === normalizedPath ||
    hub.localeUrls.sv === decodedPath
  ) || null;
}

/**
 * Get hreflang cluster for a given SRP
 */
export function getHreflangCluster(path: string, baseUrl: string): Array<{ href: string; hrefLang: string }> | null {
  const hub = getSRPHub(path);
  if (!hub) return null;
  
  return [
    { href: `${baseUrl}${hub.localeUrls.en}`, hrefLang: 'en' },
    { href: `${baseUrl}${hub.localeUrls.fi}`, hrefLang: 'fi' },
    { href: `${baseUrl}${hub.localeUrls.sv}`, hrefLang: 'sv' },
    { href: `${baseUrl}${hub.localeUrls.en}`, hrefLang: 'x-default' }
  ];
}

